<script setup lang="ts">
import { reactiveOmit } from '@vueuse/core'
import type { RadioGroupItemProps } from 'reka-ui'
import { RadioGroupItem, useForwardProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * One segment of a `RadioGroup`: a native `type="button"` with `role="radio"`, `aria-checked` and `data-state`.
 * Checked, it fills with the command cyan and takes black type, the same pair as the primary button and the
 * checked `Checkbox` (BG §22.4); unchecked it sits on the page ground. A disabled segment carries `disabled`
 * and `data-disabled` and is never chosen. Its slot is the label. No outline suppression: the `@layer base`
 * `:focus-visible` ring shows (BG §22.3).
 */
const props = defineProps<RadioGroupItemProps & { class?: HTMLAttributes['class'] }>()

const delegatedProps = reactiveOmit(props, 'class')
const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <RadioGroupItem
    data-slot="radio-group-item"
    v-bind="forwardedProps"
    :class="
      cn(
        'border-input text-muted-foreground hover:text-foreground data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground focus-visible:ring-ring/50 inline-flex min-h-9 items-center justify-center border-r px-3 py-1.5 font-mono text-xs tracking-[var(--tracking-system)] uppercase transition-colors last:border-r-0 focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50',
        props.class,
      )
    "
  >
    <slot />
  </RadioGroupItem>
</template>
